import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Box,
  Divider,
} from '@mui/material';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/errorMessage';
import { useSubmitNps } from '../hooks/useSubmitNps';

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const FIXED_QUESTION =
  'Tem alguma funcionalidade que você sente falta ou algo que poderia funcionar melhor?';

function getScoreColor(score) {
  if (score >= 9) return '#2e7d32';
  if (score >= 7) return '#ed6c02';
  return '#d32f2f';
}

function getDynamicQuestion(score) {
  if (score === null) return null;
  if (score <= 6) return 'O que podemos melhorar para te atender melhor?';
  if (score <= 8) return 'O que faria o Sigo ser ainda mais útil no seu dia a dia?';
  return 'Que funcionalidade ou situação fez você gostar mais do Sigo?';
}

export default function UserNpsModal({ open, onClose, nps }) {
  const { submit: submitNpsRequest } = useSubmitNps();
  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [score, setScore] = useState(null);
  const [dynamicAnswer, setDynamicAnswer] = useState('');
  const [fixedAnswer, setFixedAnswer] = useState('');

  useEffect(() => {
    if (open) {
      setScore(null);
      setDynamicAnswer('');
      setFixedAnswer('');
      setShowErrorMessage(false);
      setLoading(false);
    }
  }, [open]);

  const dynamicQuestion = getDynamicQuestion(score);

  const submitNps = () => {
    if (score === null) {
      setShowErrorMessage(true);
      setErrorMessage('Selecione uma nota antes de enviar.');
      return;
    }

    const questionsWithAnswers = [
      { question: dynamicQuestion, answer: dynamicAnswer },
      { question: FIXED_QUESTION, answer: fixedAnswer },
    ];

    const payload = {
      id: nps.id,
      score,
      questionsWithAnswers,
    };

    setLoading(true);
    submitNpsRequest(nps.id, payload)
      .then(() => {
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        setShowErrorMessage(true);
        setErrorMessage(error.response?.data || 'Erro ao enviar avaliação.');
        console.error('Erro ao enviar NPS:', error);
        setLoading(false);
      });
  };

  if (!nps) return null;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <Loading isToHidden={isLoading === false} />
      {showErrorMessage && (
        <ErrorMessage
          message={errorMessage}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <DialogTitle>Avalie sua experiência</DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Em uma escala de 0 a 10, o quanto você recomendaria o SIGO para outro profissional ou
              clínica?
            </Typography>
            <ToggleButtonGroup
              value={score}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  setScore(value);
                  setDynamicAnswer('');
                }
              }}
              sx={{ flexWrap: 'wrap', gap: 0.5 }}
            >
              {SCORES.map((s) => (
                <ToggleButton
                  key={s}
                  value={s}
                  sx={{
                    minWidth: 40,
                    fontWeight: 'bold',
                    '&.Mui-selected': {
                      backgroundColor: getScoreColor(s),
                      color: '#fff',
                      '&:hover': { backgroundColor: getScoreColor(s) },
                    },
                  }}
                >
                  {s}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Muito insatisfeito
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Muito satisfeito
              </Typography>
            </Box>
          </Box>

          {dynamicQuestion && (
            <>
              <Divider />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {dynamicQuestion}
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Sua resposta"
                  value={dynamicAnswer}
                  onChange={(e) => setDynamicAnswer(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={3}
                />
              </Box>
            </>
          )}

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {FIXED_QUESTION}
            </Typography>
            <TextField
              fullWidth
              placeholder="Sua resposta"
              value={fixedAnswer}
              onChange={(e) => setFixedAnswer(e.target.value)}
              variant="outlined"
              multiline
              rows={3}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={isLoading}>
          Agora não
        </Button>
        <Button variant="contained" onClick={submitNps} disabled={isLoading}>
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
